import { useMemo, useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type HelpDrawerProps = {
  title: string;
  description?: string;
  markdown: string;
  triggerLabel?: string;
  trigger?: ReactNode;
  analyticsId?: string;
  className?: string;
};

const DEFAULT_TRIGGER_LABEL = "Help";

const HelpDrawerTrigger = ({
  trigger,
  label,
}: {
  trigger?: ReactNode;
  label: string;
}): JSX.Element => {
  if (trigger) {
    return <div className="inline-flex items-center">{trigger}</div>;
  }
  return (
    <Button type="button" variant="ghost" size="sm">
      {label}
    </Button>
  );
};

export const HelpDrawer = ({
  title,
  description,
  markdown,
  trigger,
  triggerLabel = DEFAULT_TRIGGER_LABEL,
  analyticsId,
  className,
}: HelpDrawerProps): JSX.Element => {
  const [openedAt, setOpenedAt] = useState<number | null>(null);

  const parsedContent = useMemo(
    () =>
      markdown.trim().length > 0
        ? markdown
        : "_Documentation coming soon. Contact support if you need a walkthrough._",
    [markdown],
  );

  const recordAnalytics = (event: "open" | "close") => {
    if (!analyticsId || typeof window === "undefined") {
      return;
    }
    const detail = {
      id: analyticsId,
      event,
      openedAt,
      closedAt: event === "close" ? Date.now() : null,
    };
    window.dispatchEvent(new CustomEvent("braise:help-drawer", { detail }));
  };

  return (
    <Sheet
      onOpenChange={(open) => {
        if (open) {
          setOpenedAt(Date.now());
          recordAnalytics("open");
        } else {
          recordAnalytics("close");
          setOpenedAt(null);
        }
      }}
    >
      <SheetTrigger asChild>
        <span className={cn("inline-flex", className)}>
          <HelpDrawerTrigger trigger={trigger} label={triggerLabel} />
        </span>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full max-w-[420px] border-l border-border/60 bg-card px-0 sm:px-0"
      >
        <SheetHeader className="px-6 pt-6 pb-4 text-left">
          <SheetTitle className="text-lg font-semibold">{title}</SheetTitle>
          {description ? (
            <SheetDescription className="text-sm text-muted-foreground">
              {description}
            </SheetDescription>
          ) : null}
        </SheetHeader>
        <ScrollArea className="h-full px-6 pb-8">
          <article className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown>{parsedContent}</ReactMarkdown>
          </article>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
